import { Injectable, OnDestroy } from "@angular/core";
import { Subject, combineLatest, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UserSessionService } from "../user-session/user-session.service";
import { LobbyApiService } from "./lobby-api.service";
import { EventsService } from "../event/event.service";
import { LobbyNavigator } from "./lobby-navigator.service";

@Injectable({
  providedIn: "root",
})
export class GamesService implements OnDestroy {
  private userStatus: any;
  public favouriteGames: any[];
  public recentGames: any[];
  public allGames: any[];
  public primaryGames: any[];
  public lobbyName: any;
  public favouriteGameStore: any;
  private userLobbySubcriber: any;
  private flattened_array: any[];
  private flattened_All_Games_Array: any[]

  _games: Subject<any> = new Subject();
  _lobbyName: Subject<any> = new Subject();
  _destroy: Subject<any> = new Subject();

  constructor(
    private userService: UserSessionService,
    private lobbyApiService: LobbyApiService,
    private events: EventsService,
    private lobbyNavigator: LobbyNavigator
  ) {
    this.userLobbySubcriber = combineLatest([
      this.userService.getUserStatus(),
      this._lobbyName,
    ]).pipe(takeUntil(this._destroy)).subscribe(([userStatus, lobbyName]) => {
      if (userStatus && lobbyName) {
        this.updateUserStatus(userStatus);
        this.getAllGames();
      } else {
        if (!userStatus) {
          this.resetOnLogout();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userLobbySubcriber) this.userLobbySubcriber.unsubscribe();
    this._destroy.next(null);
    this._destroy.complete();
  }

  isFavourite(game_id) {
    if (!this.favouriteGames) return false;
    return game_id in this.favouriteGameStore;
  }

  getGames() {
    return this._games;
  }

  setGames(allGames) {
    this._games.next(allGames);
    this.events.publish('event:checkRecent:Fav:games')
  }

  getGameById(id: number) {
    return this.flattened_All_Games_Array.find((game) => game.id == id);

  }

  getAllGameById(id: number) {
    return this.flattened_All_Games_Array.find((game) => game.id == id);
  }

  filterGamesWithListBasedOnIds(gameFilterArray: any[]) {
    let tempArray = [...gameFilterArray];
    if (tempArray) {
      let gameArray = [];

      for (let gameInner of tempArray) {
        let value = this.flattened_All_Games_Array.find(value => gameInner.id == value.id);
        if (value) gameArray.push(value);
      }

      return of(gameArray);
    } else {
      return of([]);
    };
  }

  async getAllGames() {
    if (this.userStatus == null) return;
    this.primaryGames = this.fetchGames()
    let result = await Promise.resolve(this.getFavourites());
    this.createFavouriteStore(result.payload);
    this.favouriteGames = result.payload;
    await this.getRecentGames();
    if (!this.primaryGames["Favorites"]) this.combineAndEmit(this.primaryGames);
  }

  updateUserStatus(userStatus) {
    this.userStatus = userStatus;
  }

  setLobbyName(lobbyName) {
    this.lobbyName = lobbyName;
    this._lobbyName.next(this.lobbyName);
  }

  combineAndEmit(games, favorite = this.favouriteGames) {
    if (this.recentGames) games["Recent Games"] = this.recentGames;
    games["Favorites"] = favorite;
    this.allGames = games;
    this.setGames(this.allGames);
    this.flattened_array = Object.values(this.allGames).reduce((acc, curr) => {
      if (Array.isArray(curr)) {
        return [...acc, ...curr];
      }
      return acc;
    }, []);
  }

  async getRecentGames() {
    // Wait for the API response
    return new Promise<void>((resolve, reject) => {
      this.lobbyApiService.getLastPlayed(this.lobbyName).subscribe({
        next: (response) => {
          if (response?.payload) {
            this.recentGames = response.payload; // Set recent games
            this.events.publish('event:checkRecent:Fav:games');
            resolve(); // Resolve the promise after successful data fetch
          } else {
            console.error("Failed to load recent games");
            reject(); // Reject in case of error
          }
        },
        error: (err) => {
          console.error("Error fetching recent games: ", err);
          reject(err); // Reject on error
        }
      });
    });
  }

  createFavouriteStore(payload) {
    this.favouriteGameStore = {};
    payload?.forEach((game) => {
      this.favouriteGameStore[game.id] = "1";
    });
  }

  getFavourites() {
    return this.lobbyApiService.getFavourites().toPromise();
  }

  getRecent() {
    let recentGames: any[] = [];
    this.lobbyApiService.getLastPlayed(this.lobbyName).subscribe({
      next: (response) => {
        recentGames = response?.payload;
      }
    });

    return recentGames;
  }

  addFavourites(game_id) {
    return this.lobbyApiService.addFavourites(game_id).toPromise();
  }

  fetchGames() {
    let games: any[] = [];
    this.lobbyApiService.getGamesByLobbyName(this.lobbyName).subscribe({
      next: (response) => {
        games = response?.payload;
        // Creating a single flattened array to get all games data.
        this.flattenObjectKeysToArray(games);
      }
    });

    return games;
  }

  async removeFavouritesAndRecreate(game_id, currentKeyword = "") {
    const response = await this.removeFavourites(game_id);
    if (response.result) {
      this.favouriteGames = response.payload;
      this.createFavouriteStore(response.payload);
      this.combineAndEmit(this.primaryGames);
      if (currentKeyword === "Favorites" || currentKeyword === "FAVOURITE") {
        this.lobbyNavigator.setKeyword({
          "keyword_code": "FAVOURITE"
          });
        }
    } else {
      console.log("Error removing Fav Games", response);
    }
  }

  async addFavouritesAndRecreate(gamedata) {
    const response = await this.addFavourites(gamedata.id);
    if (response.result) {
      this.favouriteGames = response.payload;
      this.createFavouriteStore(response.payload);
      this.combineAndEmit(this.primaryGames);
    } else {
      console.log("Error adding Fav Games", response);
    }
  }

  flattenObjectKeysToArray(obj) {
    let flatArray = [];
    // Handle both arrays and nested objects
    if (obj && typeof obj === 'object') {
      Object.values(obj).forEach(value => {
        if (Array.isArray(value)) {
          flatArray = flatArray.concat(value);
        } else if (typeof value === 'object' && value !== null) {
          // Recursively flatten nested objects
          flatArray = flatArray.concat(this.flattenObjectKeysToArray(value));
        }
      });
    }
    this.flattened_All_Games_Array = flatArray;
    return flatArray;
  }

  removeRecentAndRecreate(game_id) {
    this.removeRecent(game_id);
    this.recentGames = this.recentGames.filter((game) => game.id != game_id);
  }

  removeFavourites(game_id) {
    return this.lobbyApiService.removeFavourites(game_id).toPromise();
  }

  removeRecent(game_id) {
    return this.lobbyApiService.removeRecent(game_id).toPromise();
  }

  resetOnLogout() {
    this.updateUserStatus(null);
    if (this.primaryGames && this.primaryGames["Favorites"]) {
      this.combineAndEmit(this.primaryGames, []);
    }
  }
}
